import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Video, Clock, MapPin, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useScheduledMeetingsCount } from "@/hooks/useScheduledMeetingsCount";
import { resolveDashboardRoute, type Role } from "@/lib/roles";
import { format, isToday, isTomorrow, addDays } from "date-fns";
import { fr, enUS } from "date-fns/locale";

interface CompactCalendarWidgetProps {
  maxMeetings?: number;
  className?: string;
}

const translations = {
  en: {
    upcomingMeetings: "Upcoming Meetings",
    noMeetings: "No upcoming meetings",
    today: "Today",
    tomorrow: "Tomorrow",
    viewAll: "View All",
    joinMeeting: "Join",
    scheduleMeeting: "Schedule Meeting",
    meetingsThisWeek: "meetings this week",
    in: "in",
    minutes: "min",
    hours: "h",
    days: "days",
  },
  fr: {
    upcomingMeetings: "Prochaines Réunions",
    noMeetings: "Aucune réunion à venir",
    today: "Aujourd'hui",
    tomorrow: "Demain",
    viewAll: "Voir Tout",
    joinMeeting: "Rejoindre",
    scheduleMeeting: "Planifier une Réunion",
    meetingsThisWeek: "réunions cette semaine",
    in: "dans",
    minutes: "min",
    hours: "h",
    days: "jours",
  },
};

export function CompactCalendarWidget({ maxMeetings = 3, className = "" }: CompactCalendarWidgetProps) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = translations[language as keyof typeof translations] || translations.en;
  const locale = language === "fr" ? fr : enUS;

  const { count, upcomingMeeting, isLoading } = useScheduledMeetingsCount(user?.id);

  const formatMeetingDate = (date: Date) => {
    if (isToday(date)) {
      return t.today;
    }
    if (isTomorrow(date)) {
      return t.tomorrow;
    }
    return format(date, "EEE, MMM d", { locale });
  };

  const formatMeetingTime = (date: Date) => {
    return format(date, "h:mm a", { locale });
  };

  const getTimeUntil = (date: Date) => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${t.in} ${diffMins} ${t.minutes}`;
    }
    if (diffHours < 24) {
      return `${t.in} ${diffHours}${t.hours}`;
    }
    return `${t.in} ${diffDays} ${t.days}`;
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            {t.upcomingMeetings}
          </CardTitle>
          {count > 0 && (
            <Badge variant="secondary" className="text-xs">
              {count} {t.meetingsThisWeek}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-pulse text-muted-foreground text-sm">Loading...</div>
          </div>
        ) : !upcomingMeeting ? (
          <div className="text-center py-4">
            <p className="text-muted-foreground text-sm mb-3">{t.noMeetings}</p>
            <Button size="sm" variant="outline" onClick={() => navigate("/consultant/schedule-meeting")}>
              <Calendar className="mr-2 h-4 w-4" />
              {t.scheduleMeeting}
            </Button>
          </div>
        ) : (
          <>
            <div className="p-3 border rounded-lg bg-muted/30 space-y-2">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="font-medium text-sm">{upcomingMeeting.title}</p>
                  {upcomingMeeting.dealName && (
                    <p className="text-xs text-muted-foreground">{upcomingMeeting.dealName}</p>
                  )}
                </div>
                <Badge variant="outline" className="text-xs">
                  {getTimeUntil(upcomingMeeting.scheduledAt)}
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatMeetingDate(upcomingMeeting.scheduledAt)}, {formatMeetingTime(upcomingMeeting.scheduledAt)}
                </span>
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="default" className="text-xs h-7">
                  <Video className="mr-1 h-3 w-3" />
                  {t.joinMeeting}
                </Button>
              </div>
            </div>

            {count > 1 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs" 
                onClick={() => {
                  const roles = ((user as any)?.roles as Role[] | undefined) ?? [];
                  const primary = roles[0];
                  // Role-based calendar route mapping
                  const calendarByRole: Partial<Record<Role, string>> = {
                    buyer: '/buyer/dashboard/calendar',
                    seller: '/seller/dashboard/calendar',
                    broker: '/dashboard/broker/calendar',
                    consultant: '/dashboard/consultant/calendar',
                    lender: '/dashboard/lender/calendar',
                    staff: '/staff/calendar',
                    admin: '/staff/calendar',
                  };
                  navigate(calendarByRole[primary] ?? resolveDashboardRoute(primary));
                }}
              >
                {t.viewAll} ({count - 1} more)
                <ChevronRight className="ml-1 h-3 w-3" />
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
